import { useSelector } from 'react-redux';
import { produce } from 'immer';
import { compareOnTrendsFiltersSelector } from '@store/trends/base/selectors';
import useTimeseriesQuery from './useTimeseriesQuery';
import { getFilterFromRules, getRulesFromFilter } from '@utils';
import { isAppModule } from '@utils/constants';
import { useMemo } from 'react';
import { useAtomValue } from 'jotai';
import { percentileAtom } from '../atoms';
import { percentileConfig } from '@src/utils/metrics';
import useSecondaryQuery from './secondary/useSecondaryQuery';
import { useCurrentFlowConfig } from '@src/hooks/flow/useFlowConfigs';
import { getAppFlowViewId } from '@utils/data-query/app-flow';

function useCompareTimeseriesQueries(useQueryTemplate = useTimeseriesQuery) {
  const { query, interval, metrics, realTimeOnTrends } = useQueryTemplate();
  const flowConfig = useCurrentFlowConfig();
  const compareFilters = useSelector(compareOnTrendsFiltersSelector);
  const baseFilter = query.filter;
  const percentile = useAtomValue(percentileAtom);

  const queries = useMemo(() => {
    return compareFilters.map((filter) => {
      // combine with parent filter
      const subFilter = produce(baseFilter ?? [], (draft) => {
        draft.push(...filter.rule);
      });

      const allRules = getRulesFromFilter(subFilter, true);
      const filterToApply = getFilterFromRules(allRules, true);

      return produce(query, (draft) => {
        draft.filter = filterToApply.rule;

        if (isAppModule) {
          // Regenerate viewId since the filter has changed
          query.options.viewId = getAppFlowViewId(
            undefined,
            (filterToApply.rule as any) ?? [],
            flowConfig,
          );
          if (
            percentile &&
            percentileConfig[percentile] &&
            percentileConfig[percentile].backendPercentile
          ) {
            query.options.percentile = percentileConfig[percentile].backendPercentile;
          }
        }
      });
    });
  }, [query, compareFilters, baseFilter, flowConfig, percentile]);

  return { queries, interval, metrics, compareFilters, realTimeOnTrends };
}

export function useSecondaryCompareTimeseriesQueries() {
  const params = useCompareTimeseriesQueries(useSecondaryQuery);
  return params;
}

export default useCompareTimeseriesQueries;
